import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { eventsAPI, organizationsAPI, locationsAPI, contactsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import * as XLSX from 'xlsx-js-style';
import ExcelJS from 'exceljs';
import {
  Calendar,
  ArrowLeft,
  Loader2,
  Save,
  Plus,
  X,
  Music,
  Monitor,
  Lightbulb,
  Wifi,
  Zap,
  Clock,
  Users,
  Download,
  Upload,
  Theater,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'tentative', label: 'Tentative' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const CONNECTOR_TYPES = ['Cam-Lock', 'Hubbell', 'Edison'];
const DEPARTMENT_OPTIONS = [
  { value: 'audio', label: 'Audio' },
  { value: 'video', label: 'Video' },
  { value: 'lighting', label: 'Lighting' },
  { value: 'other', label: 'Other' },
];

const SCHEDULE_TYPE_OPTIONS = [
  { value: 'Trucking', label: 'Trucking' },
  { value: 'Crew Call', label: 'Crew Call' },
  { value: 'Setup', label: 'Setup' },
  { value: 'Strike', label: 'Strike' },
  { value: 'Show', label: 'Show' },
  { value: 'Rehearsal/Tech Check', label: 'Rehearsal/Tech Check' },
  { value: 'Meal/Break', label: 'Meal/Break' },
  { value: 'Travel', label: 'Travel' },
];

const SCHEDULE_STATUS_OPTIONS = [
  { value: 'Tentative', label: 'Tentative' },
  { value: 'Confirmed', label: 'Confirmed' },
];

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isViewer } = useAuth();
  const isEditing = Boolean(id);

  useEffect(() => {
    if (isViewer) { navigate('/events'); return; }
  }, [isViewer, navigate]);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [contacts, setContacts] = useState([]);
  // Last values Flex synced (flex_core) — drives the "adopt" banner
  // when a manual edit and Flex disagree.
  const [flexCore, setFlexCore] = useState(null);

  const [form, setForm] = useState({
    event_name: '',
    flex_number: '',
    start_date: '',
    end_date: '',
    client_id: '',
    location_id: '',
    project_manager_id: '',
    account_exec_id: '',
    description: '',
    room: '',
    status: 'inquiry',
    event_contacts: [],
    schedule: [],
    // Audio
    audio_overview: '',
    audio_inputs: [],
    audio_outputs: [],
    audio_notes: '',
    // Video
    video_overview: '',
    video_inputs: [],
    video_outputs: [],
    video_notes: '',
    // Lighting
    lighting_notes: '',
    // Streaming
    streaming: {
      what: '',
      broadcast_layout: '',
      remote_calls: '',
      lower_thirds_captioning: '',
      rtmp_url: '',
      stream_key: '',
      audience_link: '',
      embed_code: '',
    },
    // Electric
    electric: {
      connections: [],
      audio_notes: '',
      video_notes: '',
      lighting_notes: '',
      other_notes: '',
    },
    // Scenic/Staging
    scenic_staging: {
      scenic: '',
      staging: '',
    },
    rigging: '',
  });

  useEffect(() => {
    loadDropdownData();
    if (isEditing) {
      loadEvent();
    }
  }, [id]);

  const loadDropdownData = async () => {
    try {
      const [orgsRes, locationsRes, contactsRes] = await Promise.all([
        organizationsAPI.list({ limit: 1000 }),
        locationsAPI.list({ limit: 1000 }),
        contactsAPI.list({ limit: 1000 }),
      ]);
      // Handle paginated response format
      setOrganizations(orgsRes.data.data || orgsRes.data);
      setLocations(locationsRes.data.data || locationsRes.data);
      setContacts(contactsRes.data.data || contactsRes.data);
    } catch (error) {
      console.error('Failed to load dropdown data');
    }
  };

  const loadEvent = async () => {
    try {
      const response = await eventsAPI.get(id);
      const data = response.data;
      setFlexCore(data.flex_core || null);
      setForm({
        event_name: data.event_name || '',
        flex_number: data.flex_number || '',
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        client_id: data.client_id || '',
        location_id: data.location_id || data.venue_id || '',
        project_manager_id: data.project_manager_id || '',
        account_exec_id: data.account_exec_id || '',
        description: data.description || '',
        room: data.room || '',
        status: data.status || 'planning',
        event_contacts: data.event_contacts || [],
        schedule: data.schedule || [],
        audio_overview: data.audio_overview || '',
        audio_inputs: data.audio_inputs || [],
        audio_outputs: data.audio_outputs || [],
        audio_notes: data.audio_notes || '',
        video_overview: data.video_overview || '',
        video_inputs: data.video_inputs || [],
        video_outputs: data.video_outputs || [],
        video_notes: data.video_notes || '',
        lighting_notes: data.lighting_notes || '',
        streaming: data.streaming || {
          what: '',
          broadcast_layout: '',
          remote_calls: '',
          lower_thirds_captioning: '',
          rtmp_url: '',
          stream_key: '',
          audience_link: '',
          embed_code: '',
        },
        electric: data.electric || {
          connections: [],
          audio_notes: '',
          video_notes: '',
          lighting_notes: '',
          other_notes: '',
        },
        scenic_staging: data.scenic_staging || {
          scenic: '',
          staging: '',
        },
        rigging: data.rigging || '',
      });
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.event_name.trim()) {
      toast.error('Event name is required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        client_id: form.client_id === 'none' || !form.client_id ? null : form.client_id,
        location_id: form.location_id === 'none' || !form.location_id ? null : form.location_id,
        project_manager_id: form.project_manager_id === 'none' || !form.project_manager_id ? null : form.project_manager_id,
        account_exec_id: form.account_exec_id === 'none' || !form.account_exec_id ? null : form.account_exec_id,
      };

      if (isEditing) {
        await eventsAPI.update(id, data);
        toast.success('Event updated');
      } else {
        const response = await eventsAPI.create(data);
        toast.success('Event created');
        navigate(`/events/${response.data.id}`);
        return;
      }
      navigate(`/events/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  // Schedule helpers
  const addScheduleItem = () => {
    setForm({
      ...form,
      schedule: [...form.schedule, { 
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
        date: '', 
        time: '', 
        duration: null,
        action: '', 
        location: '', 
        schedule_types: [],
        status: 'Tentative',
        notes: '',
        driver_id: '',
        driver_notes: '',
        truck: '',
        call_sheet: false
      }],
    });
  };

  const updateScheduleItem = (index, field, value) => {
    const newSchedule = [...form.schedule];
    newSchedule[index] = { ...newSchedule[index], [field]: value };
    setForm({ ...form, schedule: newSchedule });
  };

  const toggleScheduleType = (index, type) => {
    const newSchedule = [...form.schedule];
    const current = newSchedule[index].schedule_types || [];
    if (current.includes(type)) {
      newSchedule[index] = { ...newSchedule[index], schedule_types: current.filter(t => t !== type) };
    } else {
      newSchedule[index] = { ...newSchedule[index], schedule_types: [...current, type] };
    }
    setForm({ ...form, schedule: newSchedule });
  };

  const removeScheduleItem = (index) => {
    setForm({ ...form, schedule: form.schedule.filter((_, i) => i !== index) });
  };

  // Excel template download
  const downloadScheduleTemplate = async () => {
    try {
      // Create workbook
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'AV Factory CRM';
      workbook.created = new Date();

      // Define valid options
      const typeOptions = ['Trucking', 'Crew Call', 'Setup', 'Strike', 'Show', 'Rehearsal/Tech Check', 'Meal/Break', 'Travel'];
      const statusOptions = ['Tentative', 'Confirmed'];
      const callSheetOptions = ['Yes', 'No'];

      // Create Schedule sheet
      const scheduleSheet = workbook.addWorksheet('Schedule');
      
      // Define columns
      scheduleSheet.columns = [
        { header: 'Date', key: 'date', width: 12 },
        { header: 'Time', key: 'time', width: 8 },
        { header: 'Duration', key: 'duration', width: 10 },
        { header: 'Action', key: 'action', width: 35 },
        { header: 'Location', key: 'location', width: 20 },
        { header: 'Type', key: 'type', width: 25 },
        { header: 'Status', key: 'status', width: 12 },
        { header: 'Notes', key: 'notes', width: 30 },
        { header: 'CallSheet', key: 'callsheet', width: 10 },
      ];

      // Style header row (row 1)
      const headerRow = scheduleSheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: '1F2937' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E2E8F0' }
      };
      headerRow.border = {
        top: { style: 'thin', color: { argb: '94A3B8' } },
        bottom: { style: 'thin', color: { argb: '94A3B8' } },
        left: { style: 'thin', color: { argb: '94A3B8' } },
        right: { style: 'thin', color: { argb: '94A3B8' } }
      };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };

      // Add example data
      scheduleSheet.addRow({ date: '2024-01-15', time: '08:00', duration: 30, action: 'Truck arrives at venue', location: 'Loading Dock', type: 'Trucking', status: 'Tentative', notes: 'Use back entrance', callsheet: 'Yes' });
      scheduleSheet.addRow({ date: '2024-01-15', time: '08:30', duration: '', action: 'Crew Call & Safety Meeting', location: 'Green Room', type: 'Crew Call', status: 'Confirmed', notes: '', callsheet: 'Yes' });
      scheduleSheet.addRow({ date: '2024-01-15', time: '09:00', duration: 120, action: 'Load-in and Setup', location: 'Main Ballroom', type: 'Setup', status: 'Tentative', notes: '', callsheet: 'No' });

      // Add data validation (dropdowns) for rows 2-100
      // Type column (F) - dropdown with type options
      for (let row = 2; row <= 100; row++) {
        scheduleSheet.getCell(`F${row}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [`"${typeOptions.join(',')}"`],
          showInputMessage: true,
          promptTitle: 'Type',
          prompt: 'Select type or type comma-separated values for multiple'
        };
      }

      // Status column (G) - dropdown with status options
      for (let row = 2; row <= 100; row++) {
        scheduleSheet.getCell(`G${row}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`"${statusOptions.join(',')}"`],
          showInputMessage: true,
          promptTitle: 'Status',
          prompt: 'Select: Tentative or Confirmed'
        };
      }

      // CallSheet column (I) - dropdown with Yes/No options
      for (let row = 2; row <= 100; row++) {
        scheduleSheet.getCell(`I${row}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`"${callSheetOptions.join(',')}"`],
          showInputMessage: true,
          promptTitle: 'Call Sheet',
          prompt: 'Include on call sheet?'
        };
      }

      // Create Options reference sheet
      const optionsSheet = workbook.addWorksheet('Options');
      optionsSheet.columns = [
        { header: 'Type Options', key: 'type', width: 22 },
        { header: 'Status Options', key: 'status', width: 15 },
        { header: 'CallSheet Options', key: 'callsheet', width: 18 },
      ];

      // Style options header
      const optionsHeaderRow = optionsSheet.getRow(1);
      optionsHeaderRow.font = { bold: true, color: { argb: '1F2937' } };
      optionsHeaderRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E2E8F0' }
      };

      // Add options data
      const maxOptions = Math.max(typeOptions.length, statusOptions.length, callSheetOptions.length);
      for (let i = 0; i < maxOptions; i++) {
        optionsSheet.addRow({
          type: typeOptions[i] || '',
          status: statusOptions[i] || '',
          callsheet: callSheetOptions[i] || ''
        });
      }

      // Create Instructions sheet
      const instructionsSheet = workbook.addWorksheet('Instructions');
      instructionsSheet.columns = [
        { header: '', key: 'col1', width: 12 },
        { header: '', key: 'col2', width: 85 },
      ];

      // Add instructions content
      const instructions = [
        ['Schedule Import Template - Instructions', ''],
        ['', ''],
        ['COLUMN DESCRIPTIONS:', ''],
        ['Date', 'Format: YYYY-MM-DD (e.g., 2024-01-15)'],
        ['Time', 'Format: HH:MM in 24-hour (e.g., 08:00, 14:30)'],
        ['Duration', 'Minutes as number (e.g., 30, 60, 120)'],
        ['Action', 'Description of the schedule item'],
        ['Location', 'Where this takes place'],
        ['Type', 'DROPDOWN - Select from: Trucking, Crew Call, Setup, Strike, Show, Rehearsal/Tech Check, Meal/Break, Travel'],
        ['', 'TIP: For multiple types, type comma-separated values (e.g., "Crew Call, Setup")'],
        ['Status', 'DROPDOWN - Select: Tentative or Confirmed'],
        ['Notes', 'Additional notes for this item'],
        ['CallSheet', 'DROPDOWN - Select: Yes or No (include on crew call sheets)'],
        ['', ''],
        ['HOW TO USE DROPDOWNS:', ''],
        ['1.', 'Click on a cell in the Type, Status, or CallSheet column'],
        ['2.', 'A dropdown arrow will appear - click it to see options'],
        ['3.', 'Select your choice from the dropdown list'],
        ['', ''],
        ['TIPS:', ''],
        ['•', 'Delete the example rows (rows 2-4) before adding your data'],
        ['•', 'The "Options" sheet shows all valid dropdown values for reference'],
        ['•', 'Imported items will be ADDED to existing schedule (not replace)'],
      ];

      instructions.forEach((row, index) => {
        instructionsSheet.addRow({ col1: row[0], col2: row[1] });
      });

      // Style instructions title and section headers
      instructionsSheet.getRow(1).font = { bold: true, size: 14 };
      instructionsSheet.getRow(3).font = { bold: true };
      instructionsSheet.getRow(15).font = { bold: true };
      instructionsSheet.getRow(20).font = { bold: true };

      // Generate buffer and trigger download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Schedule_Template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded with dropdown menus');
    } catch (error) {
      console.error('Error generating template:', error);
      toast.error('Failed to generate template');
    }
  };

  // Excel file upload handler
  const fileInputRef = useRef(null);
  
  const handleScheduleUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet (Schedule)
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (jsonData.length === 0) {
          toast.error('No data found in the Excel file');
          return;
        }

        // Convert Excel data to schedule items
        const newItems = jsonData.map(row => {
          // Parse types (can be comma-separated)
          let types = [];
          if (row.Type) {
            types = row.Type.split(',').map(t => t.trim()).filter(t => t);
          }
          
          // Parse call_sheet
          const callSheet = row.CallSheet?.toLowerCase() === 'yes' || row.CallSheet === true;
          
          return {
            id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random(),
            date: row.Date || '',
            time: row.Time || '',
            duration: row.Duration ? parseInt(row.Duration) : null,
            action: row.Action || '',
            location: row.Location || '',
            schedule_types: types,
            status: row.Status || 'Tentative',
            notes: row.Notes || '',
            driver_id: '',
            driver_notes: '',
            truck: '',
            call_sheet: callSheet
          };
        });

        // Add to existing schedule
        setForm({
          ...form,
          schedule: [...form.schedule, ...newItems]
        });

        toast.success(`Imported ${newItems.length} schedule items`);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        toast.error('Failed to parse Excel file. Please check the format.');
      }
    };
    
    reader.readAsArrayBuffer(file);
    // Reset file input
    event.target.value = '';
  };

  // Sort schedule items by date then time
  const sortedSchedule = [...form.schedule].sort((a, b) => {
    const dateA = a.date || '';
    const dateB = b.date || '';
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    const timeA = a.time || '';
    const timeB = b.time || '';
    return timeA.localeCompare(timeB);
  });

  // Event contacts helpers
  const addEventContact = () => {
    setForm({
      ...form,
      event_contacts: [...form.event_contacts, { contact_id: '', event_role: '' }],
    });
  };

  const updateEventContact = (index, field, value) => {
    const newContacts = [...form.event_contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setForm({ ...form, event_contacts: newContacts });
  };

  const removeEventContact = (index) => {
    setForm({ ...form, event_contacts: form.event_contacts.filter((_, i) => i !== index) });
  };

  const getContactInfo = (contactId) => {
    return contacts.find((c) => c.id === contactId) || {};
  };

  // Audio helpers
  const addAudioInput = () => {
    setForm({
      ...form,
      audio_inputs: [...form.audio_inputs, { input_number: '', description: '', location: '', notes: '' }],
    });
  };

  const updateAudioInput = (index, field, value) => {
    const newInputs = [...form.audio_inputs];
    newInputs[index] = { ...newInputs[index], [field]: value };
    setForm({ ...form, audio_inputs: newInputs });
  };

  const removeAudioInput = (index) => {
    setForm({ ...form, audio_inputs: form.audio_inputs.filter((_, i) => i !== index) });
  };

  const addAudioOutput = () => {
    setForm({
      ...form,
      audio_outputs: [...form.audio_outputs, { output_number: '', description: '', location: '', notes: '' }],
    });
  };

  const updateAudioOutput = (index, field, value) => {
    const newOutputs = [...form.audio_outputs];
    newOutputs[index] = { ...newOutputs[index], [field]: value };
    setForm({ ...form, audio_outputs: newOutputs });
  };

  const removeAudioOutput = (index) => {
    setForm({ ...form, audio_outputs: form.audio_outputs.filter((_, i) => i !== index) });
  };

  // Video helpers
  const addVideoInput = () => {
    setForm({
      ...form,
      video_inputs: [...form.video_inputs, { input_number: '', description: '', source: '', resolution: '', video_connector: '', location: '', notes: '' }],
    });
  };

  const updateVideoInput = (index, field, value) => {
    const newInputs = [...form.video_inputs];
    newInputs[index] = { ...newInputs[index], [field]: value };
    setForm({ ...form, video_inputs: newInputs });
  };

  const removeVideoInput = (index) => {
    setForm({ ...form, video_inputs: form.video_inputs.filter((_, i) => i !== index) });
  };

  const addVideoOutput = () => {
    setForm({
      ...form,
      video_outputs: [...form.video_outputs, { output_number: '', description: '', source: '', resolution: '', video_connector: '', location: '', notes: '' }],
    });
  };

  const updateVideoOutput = (index, field, value) => {
    const newOutputs = [...form.video_outputs];
    newOutputs[index] = { ...newOutputs[index], [field]: value };
    setForm({ ...form, video_outputs: newOutputs });
  };

  const removeVideoOutput = (index) => {
    setForm({ ...form, video_outputs: form.video_outputs.filter((_, i) => i !== index) });
  };

  // Streaming helpers
  const updateStreaming = (field, value) => {
    setForm({
      ...form,
      streaming: { ...form.streaming, [field]: value },
    });
  };

  // Electric helpers
  const addElectricConnection = () => {
    setForm({
      ...form,
      electric: {
        ...form.electric,
        connections: [...form.electric.connections, { connector_type: '', amperage: '', voltage: '', phase: '', connection_location: '', for_departments: [] }],
      },
    });
  };

  const updateElectricConnection = (index, field, value) => {
    const newConnections = [...form.electric.connections];
    newConnections[index] = { ...newConnections[index], [field]: value };
    setForm({
      ...form,
      electric: { ...form.electric, connections: newConnections },
    });
  };

  const removeElectricConnection = (index) => {
    setForm({
      ...form,
      electric: {
        ...form.electric,
        connections: form.electric.connections.filter((_, i) => i !== index),
      },
    });
  };

  const toggleElectricDepartment = (index, dept) => {
    const newConnections = [...form.electric.connections];
    const current = newConnections[index].for_departments || [];
    if (current.includes(dept)) {
      newConnections[index] = { ...newConnections[index], for_departments: current.filter(d => d !== dept) };
    } else {
      newConnections[index] = { ...newConnections[index], for_departments: [...current, dept] };
    }
    setForm({
      ...form,
      electric: { ...form.electric, connections: newConnections },
    });
  };

  const updateElectricNotes = (field, value) => {
    setForm({
      ...form,
      electric: { ...form.electric, [field]: value },
    });
  };

  // Scenic/Staging helpers
  const updateScenicStaging = (field, value) => {
    setForm({
      ...form,
      scenic_staging: { ...form.scenic_staging, [field]: value },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="event-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={isEditing ? `/events/${id}` : '/events'}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Calendar className="h-8 w-8 text-emerald-400" />
            {isEditing ? 'Edit Event' : 'New Event'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Flex disagreements: Flex-synced values that differ from this
            event's fields. Adopting fills the form; saving normally
            commits it — after which future Flex changes flow through
            automatically (the sync only skips fields a human diverged). */}
        {flexCore?.last && (() => {
          const FLEX_FIELDS = [
            ['event_name', 'Event name'],
            ['start_date', 'Start date'],
            ['end_date', 'End date'],
          ];
          const diffs = FLEX_FIELDS.filter(
            ([f]) => flexCore.last[f] && form[f] !== flexCore.last[f],
          );
          const info = [
            ['client_name', 'client'], ['venue_name', 'venue'], ['pm_name', 'PM'],
          ].filter(([f]) => flexCore.last[f]);
          if (diffs.length === 0) return null;
          return (
            <Card className="border-yellow-500/40 bg-yellow-500/5">
              <CardContent className="pt-4 pb-4 space-y-2">
                <div className="text-sm font-semibold text-yellow-500">
                  Flex disagrees with this event
                </div>
                {diffs.map(([field, label]) => (
                  <div key={field} className="flex items-center gap-3 text-sm">
                    <span className="text-muted-foreground w-24">{label}</span>
                    <span className="font-medium">{flexCore.last[field]}</span>
                    <span className="text-muted-foreground">(here: {form[field] || '—'})</span>
                    <Button type="button" variant="outline" size="sm"
                      onClick={() => setForm({ ...form, [field]: flexCore.last[field] })}>
                      Adopt
                    </Button>
                  </div>
                ))}
                {info.length > 0 && (
                  <div className="text-xs text-muted-foreground pt-1">
                    Flex also reports: {info.map(([f, l]) => `${l} ${flexCore.last[f]}`).join(' · ')}
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  Adopt fills the field; save the form to keep it. Adopted fields follow
                  Flex automatically from then on.
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Basic Information */}
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="event_name">Event Name *</Label>
                <Input
                  id="event_name"
                  value={form.event_name}
                  onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                  placeholder="Enter event name"
                  className="bg-background/50"
                  data-testid="event-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="flex_number">Flex #</Label>
                <Input
                  id="flex_number"
                  value={form.flex_number}
                  onChange={(e) => setForm({ ...form, flex_number: e.target.value })}
                  placeholder="Internal tracking number"
                  className="bg-background/50 font-mono"
                  data-testid="flex-number-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="bg-background/50"
                  data-testid="start-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="bg-background/50"
                  data-testid="end-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(value) => setForm({ ...form, status: value })}
                >
                  <SelectTrigger className="bg-background/50" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_id">Client</Label>
                <Select
                  value={form.client_id || 'none'}
                  onValueChange={(value) => setForm({ ...form, client_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-background/50" data-testid="client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No client</SelectItem>
                    {organizations
                      .filter((org) => org.org_roles?.includes('client'))
                      .map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.organization_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location_id">Location</Label>
                <Select
                  value={form.location_id || 'none'}
                  onValueChange={(value) => setForm({ ...form, location_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-background/50" data-testid="location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No location</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.location_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="project_manager_id">Project Manager</Label>
                <Select
                  value={form.project_manager_id || 'none'}
                  onValueChange={(value) => setForm({ ...form, project_manager_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-background/50" data-testid="pm-select">
                    <SelectValue placeholder="No PM assigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No PM assigned</SelectItem>
                    {contacts
                      .filter(contact => contact.role_tags && contact.role_tags.includes('AVF Staff'))
                      .map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.contact_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="account_exec_id">Account Executive</Label>
                <Select
                  value={form.account_exec_id || 'none'}
                  onValueChange={(value) => setForm({ ...form, account_exec_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-background/50" data-testid="ae-select">
                    <SelectValue placeholder="No AE assigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No AE assigned</SelectItem>
                    {contacts
                      .filter(contact => contact.role_tags && contact.role_tags.includes('AVF Staff'))
                      .map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.contact_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Sections Tabs */}
        <Card className="border-border/50 bg-card/50">
          <CardContent className="pt-6">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid grid-cols-4 lg:grid-cols-9 gap-1 h-auto bg-muted/50 p-1">
                <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
                <TabsTrigger value="schedule" className="text-xs">Schedule</TabsTrigger>
                <TabsTrigger value="contacts" className="text-xs">Contacts</TabsTrigger>
                <TabsTrigger value="audio" className="text-xs">Audio</TabsTrigger>
                <TabsTrigger value="video" className="text-xs">Video</TabsTrigger>
                <TabsTrigger value="lighting" className="text-xs">Lighting</TabsTrigger>
                <TabsTrigger value="streaming" className="text-xs">Streaming</TabsTrigger>
                <TabsTrigger value="electric" className="text-xs">Electric</TabsTrigger>
                <TabsTrigger value="scenic" className="text-xs">Scenic/Staging</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="room">Room / Space</Label>
                  <Input
                    id="room"
                    value={form.room}
                    onChange={(e) => setForm({ ...form, room: e.target.value })}
                    placeholder="e.g., Grand Ballroom, Conference Room A"
                    className="bg-background/50"
                    data-testid="room-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Event description and notes..."
                    rows={6}
                    className="bg-background/50"
                    data-testid="description-input"
                  />
                </div>
              </TabsContent>

              {/* Schedule Tab */}
              <TabsContent value="schedule" className="space-y-4 mt-6">
                <div className="flex justify-between items-center flex-wrap gap-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Clock className="h-5 w-5 text-cyan-400" />
                    Event Schedule
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={downloadScheduleTemplate}>
                      <Download className="h-4 w-4 mr-2" /> Template
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" /> Import Excel
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleScheduleUpload}
                      className="hidden"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={addScheduleItem}>
                      <Plus className="h-4 w-4 mr-2" /> Add Item
                    </Button>
                  </div>
                </div>
                {form.schedule.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-28">Date</TableHead>
                          <TableHead className="w-20">Time</TableHead>
                          <TableHead className="w-20">Duration</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead className="w-48">Type</TableHead>
                          <TableHead className="w-28">Status</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead className="w-20">Call Sheet</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedSchedule.map((item) => {
                          const originalIndex = form.schedule.findIndex(s => s.id === item.id || (s.date === item.date && s.time === item.time && s.action === item.action));
                          return (
                            <TableRow key={item.id || originalIndex}>
                              <TableCell>
                                <Input
                                  type="date"
                                  value={item.date || ''}
                                  onChange={(e) => updateScheduleItem(originalIndex, 'date', e.target.value)}
                                  className="bg-background/50 w-28"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="time"
                                  value={item.time || ''}
                                  onChange={(e) => updateScheduleItem(originalIndex, 'time', e.target.value)}
                                  className="bg-background/50 w-20"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={item.duration || ''}
                                  onChange={(e) => updateScheduleItem(originalIndex, 'duration', e.target.value ? parseInt(e.target.value) : null)}
                                  placeholder="min"
                                  className="bg-background/50 w-20"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.action || ''}
                                  onChange={(e) => updateScheduleItem(originalIndex, 'action', e.target.value)}
                                  placeholder="Action"
                                  className="bg-background/50"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.location || ''}
                                  onChange={(e) => updateScheduleItem(originalIndex, 'location', e.target.value)}
                                  placeholder="Location"
                                  className="bg-background/50"
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {SCHEDULE_TYPE_OPTIONS.map((type) => (
                                    <label key={type.value} className="flex items-center gap-1 cursor-pointer">
                                      <Checkbox
                                        checked={(item.schedule_types || []).includes(type.value)}
                                        onCheckedChange={() => toggleScheduleType(originalIndex, type.value)}
                                      />
                                      <span className="text-xs">{type.label}</span>
                                    </label>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={item.status || 'Tentative'}
                                  onValueChange={(value) => updateScheduleItem(originalIndex, 'status', value)}
                                >
                                  <SelectTrigger className="bg-background/50 w-28">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SCHEDULE_STATUS_OPTIONS.map((s) => (
                                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.notes || ''}
                                  onChange={(e) => updateScheduleItem(originalIndex, 'notes', e.target.value)}
                                  placeholder="Notes"
                                  className="bg-background/50"
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Checkbox
                                  checked={item.call_sheet || false}
                                  onCheckedChange={(checked) => updateScheduleItem(originalIndex, 'call_sheet', checked)}
                                />
                              </TableCell>
                              <TableCell>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeScheduleItem(originalIndex)}>
                                  <X className="h-4 w-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No schedule items added yet</p>
                )}
              </TabsContent>

              {/* Contacts Tab */}
              <TabsContent value="contacts" className="space-y-4 mt-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5 text-cyan-400" />
                    Event Contacts
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={addEventContact}>
                    <Plus className="h-4 w-4 mr-2" /> Add Contact
                  </Button>
                </div>
                {form.event_contacts.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Contact</TableHead>
                        <TableHead>Event Role</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.event_contacts.map((ec, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Select
                              value={ec.contact_id || 'none'}
                              onValueChange={(value) => updateEventContact(index, 'contact_id', value === 'none' ? '' : value)}
                            >
                              <SelectTrigger className="bg-background/50">
                                <SelectValue placeholder="Select contact" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select contact</SelectItem>
                                {contacts.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>
                                    {c.contact_name} {c.organization_name ? `(${c.organization_name})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={ec.event_role}
                              onChange={(e) => updateEventContact(index, 'event_role', e.target.value)}
                              placeholder="Role (e.g., Client Rep, Tech Director)"
                              className="bg-background/50"
                            />
                          </TableCell>
                          <TableCell>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeEventContact(index)}>
                              <X className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No contacts added yet</p>
                )}
              </TabsContent>

              {/* Audio Tab */}
              <TabsContent value="audio" className="space-y-6 mt-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Music className="h-5 w-5 text-blue-400" />
                  Audio
                </h3>
                
                <div className="space-y-2">
                  <Label>Overview</Label>
                  <Textarea
                    value={form.audio_overview}
                    onChange={(e) => setForm({ ...form, audio_overview: e.target.value })}
                    placeholder="Audio system overview..."
                    rows={3}
                    className="bg-background/50"
                  />
                </div>

                {/* Audio Inputs */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-base">Inputs</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addAudioInput}>
                      <Plus className="h-4 w-4 mr-1" /> Add Input
                    </Button>
                  </div>
                  {form.audio_inputs.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">Input #</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {form.audio_inputs.map((input, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Input value={input.input_number} onChange={(e) => updateAudioInput(index, 'input_number', e.target.value)} className="bg-background/50 w-20" />
                            </TableCell>
                            <TableCell>
                              <Input value={input.description} onChange={(e) => updateAudioInput(index, 'description', e.target.value)} className="bg-background/50" />
                            </TableCell>
                            <TableCell>
                              <Input value={input.location} onChange={(e) => updateAudioInput(index, 'location', e.target.value)} className="bg-background/50" />
                            </TableCell>
                            <TableCell>
                              <Input value={input.notes} onChange={(e) => updateAudioInput(index, 'notes', e.target.value)} className="bg-background/50" />
                            </TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeAudioInput(index)}>
                                <X className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>

                {/* Audio Outputs */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-base">Outputs</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addAudioOutput}>
                      <Plus className="h-4 w-4 mr-1" /> Add Output
                    </Button>
                  </div>
                  {form.audio_outputs.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">Output #</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Notes</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {form.audio_outputs.map((output, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Input value={output.output_number} onChange={(e) => updateAudioOutput(index, 'output_number', e.target.value)} className="bg-background/50 w-20" />
                            </TableCell>
                            <TableCell>
                              <Input value={output.description} onChange={(e) => updateAudioOutput(index, 'description', e.target.value)} className="bg-background/50" />
                            </TableCell>
                            <TableCell>
                              <Input value={output.location} onChange={(e) => updateAudioOutput(index, 'location', e.target.value)} className="bg-background/50" />
                            </TableCell>
                            <TableCell>
                              <Input value={output.notes} onChange={(e) => updateAudioOutput(index, 'notes', e.target.value)} className="bg-background/50" />
                            </TableCell>
                            <TableCell>
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeAudioOutput(index)}>
                                <X className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={form.audio_notes}
                    onChange={(e) => setForm({ ...form, audio_notes: e.target.value })}
                    placeholder="Additional audio notes..."
                    rows={3}
                    className="bg-background/50"
                  />
                </div>
              </TabsContent>

              {/* Video Tab */}
              <TabsContent value="video" className="space-y-6 mt-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Monitor className="h-5 w-5 text-red-400" />
                  Video
                </h3>
                
                <div className="space-y-2">
                  <Label>Overview</Label>
                  <Textarea
                    value={form.video_overview}
                    onChange={(e) => setForm({ ...form, video_overview: e.target.value })}
                    placeholder="Video system overview..."
                    rows={3}
                    className="bg-background/50"
                  />
                </div>

                {/* Video Inputs */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-base">Inputs</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addVideoInput}>
                      <Plus className="h-4 w-4 mr-1" /> Add Input
                    </Button>
                  </div>
                  {form.video_inputs.length > 0 && (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">Input #</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead>Resolution</TableHead>
                            <TableHead>Connector</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Notes</TableHead>
                            <TableHead className="w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {form.video_inputs.map((input, index) => (
                            <TableRow key={index}>
                              <TableCell><Input value={input.input_number} onChange={(e) => updateVideoInput(index, 'input_number', e.target.value)} className="bg-background/50 w-16" /></TableCell>
                              <TableCell><Input value={input.description} onChange={(e) => updateVideoInput(index, 'description', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Input value={input.source} onChange={(e) => updateVideoInput(index, 'source', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Input value={input.resolution} onChange={(e) => updateVideoInput(index, 'resolution', e.target.value)} className="bg-background/50" placeholder="e.g., 1080p" /></TableCell>
                              <TableCell><Input value={input.video_connector} onChange={(e) => updateVideoInput(index, 'video_connector', e.target.value)} className="bg-background/50" placeholder="HDMI/SDI" /></TableCell>
                              <TableCell><Input value={input.location} onChange={(e) => updateVideoInput(index, 'location', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Input value={input.notes} onChange={(e) => updateVideoInput(index, 'notes', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Button type="button" variant="ghost" size="icon" onClick={() => removeVideoInput(index)}><X className="h-4 w-4 text-destructive" /></Button></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Video Outputs */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-base">Outputs</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addVideoOutput}>
                      <Plus className="h-4 w-4 mr-1" /> Add Output
                    </Button>
                  </div>
                  {form.video_outputs.length > 0 && (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">Output #</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead>Resolution</TableHead>
                            <TableHead>Connector</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Notes</TableHead>
                            <TableHead className="w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {form.video_outputs.map((output, index) => (
                            <TableRow key={index}>
                              <TableCell><Input value={output.output_number} onChange={(e) => updateVideoOutput(index, 'output_number', e.target.value)} className="bg-background/50 w-16" /></TableCell>
                              <TableCell><Input value={output.description} onChange={(e) => updateVideoOutput(index, 'description', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Input value={output.source} onChange={(e) => updateVideoOutput(index, 'source', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Input value={output.resolution} onChange={(e) => updateVideoOutput(index, 'resolution', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Input value={output.video_connector} onChange={(e) => updateVideoOutput(index, 'video_connector', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Input value={output.location} onChange={(e) => updateVideoOutput(index, 'location', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Input value={output.notes} onChange={(e) => updateVideoOutput(index, 'notes', e.target.value)} className="bg-background/50" /></TableCell>
                              <TableCell><Button type="button" variant="ghost" size="icon" onClick={() => removeVideoOutput(index)}><X className="h-4 w-4 text-destructive" /></Button></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={form.video_notes}
                    onChange={(e) => setForm({ ...form, video_notes: e.target.value })}
                    placeholder="Additional video notes..."
                    rows={3}
                    className="bg-background/50"
                  />
                </div>
              </TabsContent>

              {/* Lighting Tab */}
              <TabsContent value="lighting" className="space-y-4 mt-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-yellow-400" />
                  Lighting
                </h3>
                <div className="space-y-2">
                  <Label>Lighting Notes</Label>
                  <Textarea
                    value={form.lighting_notes}
                    onChange={(e) => setForm({ ...form, lighting_notes: e.target.value })}
                    placeholder="Lighting requirements, fixtures, positions, cues..."
                    rows={8}
                    className="bg-background/50"
                    data-testid="lighting-notes-input"
                  />
                </div>
              </TabsContent>

              {/* Streaming Tab */}
              <TabsContent value="streaming" className="space-y-4 mt-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Wifi className="h-5 w-5 text-green-400" />
                  Streaming
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>What?</Label>
                    <Textarea
                      value={form.streaming.what}
                      onChange={(e) => updateStreaming('what', e.target.value)}
                      placeholder="What is being streamed? (e.g., main stage, breakout sessions)"
                      rows={2}
                      className="bg-background/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Broadcast Layout</Label>
                    <Textarea
                      value={form.streaming.broadcast_layout}
                      onChange={(e) => updateStreaming('broadcast_layout', e.target.value)}
                      placeholder="Layout description (PIP, side-by-side, full screen, etc.)"
                      rows={2}
                      className="bg-background/50"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Remote Calls</Label>
                    <Textarea
                      value={form.streaming.remote_calls}
                      onChange={(e) => updateStreaming('remote_calls', e.target.value)}
                      placeholder="Remote call details (Zoom, Teams, etc.)"
                      rows={2}
                      className="bg-background/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Lower Thirds / Captioning</Label>
                    <Textarea
                      value={form.streaming.lower_thirds_captioning}
                      onChange={(e) => updateStreaming('lower_thirds_captioning', e.target.value)}
                      placeholder="Lower thirds, captions, graphics requirements"
                      rows={2}
                      className="bg-background/50"
                    />
                  </div>
                </div>

                <div className="border-t border-border/50 pt-4 mt-4">
                  <h4 className="font-medium mb-4">Streaming Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>RTMP URL</Label>
                      <Input
                        value={form.streaming.rtmp_url}
                        onChange={(e) => updateStreaming('rtmp_url', e.target.value)}
                        placeholder="rtmp://..."
                        className="bg-background/50 font-mono text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stream Key</Label>
                      <Input
                        value={form.streaming.stream_key}
                        onChange={(e) => updateStreaming('stream_key', e.target.value)}
                        placeholder="Stream key"
                        className="bg-background/50 font-mono text-sm"
                        type="password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Audience Link</Label>
                      <Input
                        value={form.streaming.audience_link}
                        onChange={(e) => updateStreaming('audience_link', e.target.value)}
                        placeholder="https://..."
                        className="bg-background/50 font-mono text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Embed Code</Label>
                      <Textarea
                        value={form.streaming.embed_code}
                        onChange={(e) => updateStreaming('embed_code', e.target.value)}
                        placeholder="<iframe>...</iframe>"
                        rows={2}
                        className="bg-background/50 font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Electric Tab */}
              <TabsContent value="electric" className="space-y-6 mt-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Zap className="h-5 w-5 text-orange-400" />
                  Electric
                </h3>

                {/* Connections Table */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-base">Power Connections</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addElectricConnection}>
                      <Plus className="h-4 w-4 mr-1" /> Add Connection
                    </Button>
                  </div>
                  {form.electric.connections.length > 0 && (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Connector Type</TableHead>
                            <TableHead>Amperage</TableHead>
                            <TableHead>Voltage</TableHead>
                            <TableHead>Phase</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>For</TableHead>
                            <TableHead className="w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {form.electric.connections.map((conn, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Select
                                  value={conn.connector_type || 'none'}
                                  onValueChange={(value) => updateElectricConnection(index, 'connector_type', value === 'none' ? '' : value)}
                                >
                                  <SelectTrigger className="bg-background/50">
                                    <SelectValue placeholder="Select type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">Select type</SelectItem>
                                    {CONNECTOR_TYPES.map((type) => (
                                      <SelectItem key={type} value={type}>{type}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Input value={conn.amperage} onChange={(e) => updateElectricConnection(index, 'amperage', e.target.value)} placeholder="e.g., 200A" className="bg-background/50" />
                              </TableCell>
                              <TableCell>
                                <Input value={conn.voltage} onChange={(e) => updateElectricConnection(index, 'voltage', e.target.value)} placeholder="e.g., 208V" className="bg-background/50" />
                              </TableCell>
                              <TableCell>
                                <Input value={conn.phase} onChange={(e) => updateElectricConnection(index, 'phase', e.target.value)} placeholder="e.g., 3-phase" className="bg-background/50" />
                              </TableCell>
                              <TableCell>
                                <Input value={conn.connection_location} onChange={(e) => updateElectricConnection(index, 'connection_location', e.target.value)} placeholder="Location" className="bg-background/50" />
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-2">
                                  {DEPARTMENT_OPTIONS.map((dept) => (
                                    <label key={dept.value} className="flex items-center gap-1.5 cursor-pointer">
                                      <Checkbox
                                        checked={(conn.for_departments || []).includes(dept.value)}
                                        onCheckedChange={() => toggleElectricDepartment(index, dept.value)}
                                      />
                                      <span className="text-xs">{dept.label}</span>
                                    </label>
                                  ))}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeElectricConnection(index)}>
                                  <X className="h-4 w-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Department Notes */}
                <div className="border-t border-border/50 pt-4 mt-4">
                  <h4 className="font-medium mb-4">Department Notes</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Audio Notes</Label>
                      <Textarea
                        value={form.electric.audio_notes}
                        onChange={(e) => updateElectricNotes('audio_notes', e.target.value)}
                        placeholder="Audio power notes..."
                        rows={3}
                        className="bg-background/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Video Notes</Label>
                      <Textarea
                        value={form.electric.video_notes}
                        onChange={(e) => updateElectricNotes('video_notes', e.target.value)}
                        placeholder="Video power notes..."
                        rows={3}
                        className="bg-background/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Lighting Notes</Label>
                      <Textarea
                        value={form.electric.lighting_notes}
                        onChange={(e) => updateElectricNotes('lighting_notes', e.target.value)}
                        placeholder="Lighting power notes..."
                        rows={3}
                        className="bg-background/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Other Notes</Label>
                      <Textarea
                        value={form.electric.other_notes}
                        onChange={(e) => updateElectricNotes('other_notes', e.target.value)}
                        placeholder="Other power notes..."
                        rows={3}
                        className="bg-background/50"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Scenic/Staging Tab */}
              <TabsContent value="scenic" className="space-y-4 mt-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Theater className="h-5 w-5 text-pink-400" />
                  Scenic / Staging
                </h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Scenic</Label>
                    <Textarea
                      value={form.scenic_staging.scenic}
                      onChange={(e) => updateScenicStaging('scenic', e.target.value)}
                      placeholder="Scenic elements, backdrops, set pieces, drapes..."
                      rows={5}
                      className="bg-background/50"
                      data-testid="scenic-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Staging</Label>
                    <Textarea
                      value={form.scenic_staging.staging}
                      onChange={(e) => updateScenicStaging('staging', e.target.value)}
                      placeholder="Stage dimensions, risers, podiums, furniture..."
                      rows={5}
                      className="bg-background/50"
                      data-testid="staging-input"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" asChild>
            <Link to={isEditing ? `/events/${id}` : '/events'}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving} data-testid="submit-event-btn">
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? 'Save Changes' : 'Create Event'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
